import { createJSONStorage, persist } from "zustand/middleware"
import { createStore, type StoreApi } from "zustand/vanilla"

import {
  type AuthorizationChangedEvent,
  type BasicCredentials,
  createBasicAuthorization,
  getRemoteRevision,
  type RemoteClient,
  RemoteProtocolError,
  resumeRemoteAuthorization,
  subscribeRemoteRevision,
  suspendRemoteAuthorization,
} from "../remote/remote-client"
import { defineRemoteCommand } from "../remote/remote-command"
import type { Storage } from "../storage/storage"

export type SessionStatus = "signed-out" | "signed-in" | "authentication-required"

export type SessionLoginFailureReason =
  | "unknown"
  | "no-client-id"
  | "incomplete-authentication-data"
  | "unknown-authentication-data"
  | "external-internal-user-conflict"
  | "session-lost-and-not-allowed-to-restore"

export interface SessionUser {
  readonly username: string
  readonly imageUrl?: string
}

export interface SessionLoginOptions {
  readonly signal?: AbortSignal
  readonly rememberAuthentication?: boolean
}

export interface SessionLoginFailure {
  readonly reason: SessionLoginFailureReason
  readonly messages: readonly string[]
}

export type SessionLoginResult =
  | {
      readonly success: true
      readonly user: SessionUser
    }
  | {
      readonly success: false
      readonly failure: SessionLoginFailure
    }

export class SessionAuthenticationInProgressError extends Error {
  constructor() {
    super("A session authentication attempt is already in progress.")
    this.name = "SessionAuthenticationInProgressError"
  }
}

export class SessionAlreadyAuthenticatedError extends Error {
  constructor() {
    super("The session is already signed in.")
    this.name = "SessionAlreadyAuthenticatedError"
  }
}

export class SessionAuthenticationCancelledError extends Error {
  constructor() {
    super("The session authentication attempt was cancelled.")
    this.name = "SessionAuthenticationCancelledError"
  }
}

interface SessionBaseOptions {
  storageKey?: string
  user?: SessionUser
  productVersion?: string
  appIdentifier?: string
}

export type SessionOptions = SessionBaseOptions &
  (
    | {
        authorization?: string
        basicCredentials?: never
      }
    | {
        authorization?: never
        basicCredentials?: BasicCredentials
      }
  )

export interface SessionSnapshot {
  readonly status: SessionStatus
  readonly user: SessionUser | null
  readonly isRestored: boolean
  readonly isAuthenticating: boolean
}

export interface SessionAccessContext {
  readonly revision: number
  readonly baseUrl: string
}

interface SessionStoreState {
  authorization?: string
  persistAuthorization: boolean
  snapshot: SessionSnapshot
}

interface PersistedSessionState {
  authorization?: string
  status: SessionStatus
  user: SessionUser | null
}

interface CloudLoginRequest {
  productVersion: string | null
  userId: string
  password: string
  nietAutoLogin: true
  appLogin: true
  isExternalUser: false
  authenticationToken: null
  delayedLicense: false
  appIdentifier: string | null
  keepDuplicateUser: false
  appAutoLogin: boolean
}

interface CloudLoginResult {
  success?: unknown
  authenticationToken?: unknown
  failure?: unknown
  userId?: unknown
}

const cloudLoginCommand = defineRemoteCommand<CloudLoginRequest, CloudLoginResult>("basis/inloggen/login")

type SessionStore = StoreApi<SessionStoreState> & {
  persist: {
    hasHydrated(): boolean
    rehydrate(): Promise<void> | void
  }
}

const defaultStorageKey = "session"
const persistenceVersion = 1

export class Session {
  private readonly store: SessionStore
  private readonly restoredPromise: Promise<void>
  private readonly productVersion: string | null
  private readonly appIdentifier: string | null
  private resolveRestored!: () => void
  private authenticationAttempt = 0
  private authenticationAbortController: AbortController | null = null
  private restorationSuperseded = false
  private accessContextSnapshot!: SessionAccessContext
  private readonly accessContextListeners = new Set<() => void>()

  constructor(
    private readonly remote: RemoteClient,
    storage: Storage,
    options: SessionOptions = {}
  ) {
    this.productVersion = options.productVersion ?? null
    this.appIdentifier = options.appIdentifier ?? null
    const configuredAuthorization = normalizeAuthorization(options.authorization)
    const hasConfiguredBasicCredentials =
      options.basicCredentials != null && options.basicCredentials.username.length > 0
    const initialUser =
      options.user ?? (hasConfiguredBasicCredentials ? { username: options.basicCredentials!.username } : null)

    let initialAuthorization = configuredAuthorization
    if (hasConfiguredBasicCredentials) {
      this.remote.setBasicCredentials(options.basicCredentials!.username, options.basicCredentials!.password)
      initialAuthorization = this.remote.getAuthorization()
    } else if (initialAuthorization != null) {
      this.remote.setAuthorization(initialAuthorization)
    }
    this.accessContextSnapshot = createAccessContext(remote)

    const initialStatus: SessionStatus = initialAuthorization == null ? "signed-out" : "signed-in"
    const initialState: SessionStoreState = {
      authorization: initialAuthorization,
      persistAuthorization: configuredAuthorization != null,
      snapshot: createSnapshot(initialStatus, initialUser, false),
    }
    const hasConfiguredAuthorization = configuredAuthorization != null || hasConfiguredBasicCredentials

    this.restoredPromise = new Promise<void>((resolve) => {
      this.resolveRestored = resolve
    })

    const zustandStorage = createJSONStorage<PersistedSessionState>(() => storage)
    this.store = createStore<SessionStoreState>()(
      persist<SessionStoreState, [], [], PersistedSessionState>(() => initialState, {
        name: options.storageKey ?? defaultStorageKey,
        storage: zustandStorage,
        version: persistenceVersion,
        skipHydration: true,
        onRehydrateStorage: () => () => this.markRestored(),
        partialize: createPersistedState,
        merge: (persistedState, currentState) =>
          this.restorationSuperseded
            ? currentState
            : mergePersistedState(persistedState, currentState, hasConfiguredAuthorization, options.user),
      })
    ) as SessionStore

    this.remote.subscribeAuthenticationRequired(() => this.requireAuthentication())
    this.remote.subscribeAuthorization((event) => this.handleAuthorizationChanged(event))
    subscribeRemoteRevision(this.remote, () => this.handleRemoteRevisionChanged())
    this.store.subscribe((state, previousState) => {
      // A suspended or resumed authorization is already applied to the remote client without a new access context.
      if (
        state.authorization !== previousState.authorization &&
        state.authorization !== this.remote.getAuthorization()
      ) {
        this.remote.setAuthorization(state.authorization)
      }
    })
    this.store.persist.rehydrate()

    if (this.store.persist.hasHydrated()) this.markRestored()
  }

  get status() {
    return this.snapshot.status
  }

  get user() {
    return this.snapshot.user
  }

  get isRestored() {
    return this.snapshot.isRestored
  }

  get isAuthenticating() {
    return this.snapshot.isAuthenticating
  }

  get snapshot() {
    return this.store.getState().snapshot
  }

  get accessContext() {
    return this.accessContextSnapshot
  }

  subscribeAccessContext(listener: () => void) {
    this.accessContextListeners.add(listener)
    return () => {
      this.accessContextListeners.delete(listener)
    }
  }

  whenRestored() {
    return this.restoredPromise
  }

  async login(credentials: BasicCredentials, options: SessionLoginOptions = {}): Promise<SessionLoginResult> {
    if (!this.isRestored) {
      await this.whenRestored()
    }

    if (this.isAuthenticating) {
      throw new SessionAuthenticationInProgressError()
    }
    if (this.status === "signed-in") {
      throw new SessionAlreadyAuthenticatedError()
    }

    const username = credentials.username.trim()
    if (username.length === 0) {
      return createLoginFailure("incomplete-authentication-data")
    }

    const previousState = this.store.getState()
    const attempt = ++this.authenticationAttempt
    const abortController = new AbortController()
    this.authenticationAbortController = abortController
    const removeExternalAbortListener = forwardAbort(options.signal, abortController)

    this.update({
      status: previousState.snapshot.status,
      user: previousState.snapshot.user,
      isAuthenticating: true,
    })

    try {
      const response = await this.remote.commands.execute(
        cloudLoginCommand(createCloudLoginRequest(credentials, this.productVersion, this.appIdentifier, options)),
        { signal: abortController.signal }
      )

      this.throwIfAuthenticationAttemptChanged(attempt)
      const result = parseCloudLoginResult(response)
      if (!result.success) {
        this.restoreAfterFailedLogin(previousState)
        return result
      }

      const authenticatedUsername = result.userId ?? username
      const previousUser = previousState.snapshot.user
      const user = createAuthenticatedUser(
        authenticatedUsername,
        previousUser?.username === authenticatedUsername ? previousUser.imageUrl : undefined
      )
      const authorization = createBearerAuthorization(result.authenticationToken)
      // Only the user whose authorization expired may continue its access context. An unknown previous
      // user, another user, or a suspension ended by a server or configured change starts a new one.
      if (
        previousState.snapshot.status === "authentication-required" &&
        previousUser?.username === authenticatedUsername
      ) {
        resumeRemoteAuthorization(this.remote, authorization)
      }
      this.update({
        authorization,
        persistAuthorization: true,
        status: "signed-in",
        user,
        isAuthenticating: false,
      })

      return Object.freeze({
        success: true,
        user: this.user!,
      })
    } catch (error) {
      if (attempt === this.authenticationAttempt) {
        this.restoreAfterFailedLogin(previousState)
      }
      throw error
    } finally {
      removeExternalAbortListener()
      if (attempt === this.authenticationAttempt) {
        this.authenticationAbortController = null
      }
    }
  }

  setBasicCredentials(credentials: BasicCredentials, user?: SessionUser) {
    if (credentials.username.length === 0) {
      this.signOut()
      return
    }

    this.supersedeRestoration()
    this.cancelAuthenticationAttempt()
    const currentUser = this.user?.username === credentials.username ? this.user : null
    this.update({
      authorization: createBasicAuthorization(credentials.username, credentials.password),
      persistAuthorization: false,
      status: "signed-in",
      user: user ?? currentUser ?? { username: credentials.username },
      isAuthenticating: false,
    })
  }

  setAuthorization(authorization: string, user?: SessionUser) {
    const value = normalizeAuthorization(authorization)
    if (value == null) {
      this.signOut()
      return
    }

    this.supersedeRestoration()
    this.cancelAuthenticationAttempt()
    this.update({
      authorization: value,
      persistAuthorization: true,
      status: "signed-in",
      user: user ?? this.user,
      isAuthenticating: false,
    })
  }

  setUser(user: SessionUser | null) {
    this.update({
      status: this.status,
      user,
    })
  }

  signOut() {
    this.supersedeRestoration()
    this.cancelAuthenticationAttempt()
    this.update({
      authorization: undefined,
      persistAuthorization: false,
      status: "signed-out",
      user: null,
      isAuthenticating: false,
    })
    // Ends a suspended access context too, whose authorization was already withheld.
    this.remote.setAuthorization(undefined)
  }

  subscribe(listener: () => void) {
    let snapshot = this.snapshot
    return this.store.subscribe((state) => {
      if (state.snapshot === snapshot) return
      snapshot = state.snapshot
      listener()
    })
  }

  private update(options: {
    authorization?: string
    persistAuthorization?: boolean
    status: SessionStatus
    user: SessionUser | null
    isAuthenticating?: boolean
  }) {
    const state = this.store.getState()
    const nextAuthorization = Object.hasOwn(options, "authorization") ? options.authorization : state.authorization
    const nextPersistAuthorization = options.persistAuthorization ?? state.persistAuthorization
    const nextSnapshot = createSnapshot(
      options.status,
      options.user,
      state.snapshot.isRestored,
      options.isAuthenticating ?? state.snapshot.isAuthenticating
    )

    if (
      state.authorization === nextAuthorization &&
      state.persistAuthorization === nextPersistAuthorization &&
      snapshotsEqual(state.snapshot, nextSnapshot)
    ) {
      return
    }

    this.store.setState({
      authorization: nextAuthorization,
      persistAuthorization: nextPersistAuthorization,
      snapshot: nextSnapshot,
    })
  }

  private requireAuthentication() {
    if (this.status === "signed-out") return

    // Keeps the access context: the same user can resume it by logging in again.
    suspendRemoteAuthorization(this.remote)
    this.update({
      authorization: undefined,
      persistAuthorization: false,
      status: "authentication-required",
      user: this.user,
    })
  }

  private handleAuthorizationChanged(event: AuthorizationChangedEvent) {
    if (event.source !== "response") return

    const authorization = normalizeAuthorization(event.authorization)
    if (authorization == null) {
      this.requireAuthentication()
      return
    }

    this.update({
      authorization,
      persistAuthorization: true,
      status: "signed-in",
      user: this.user,
    })
  }

  private handleRemoteRevisionChanged() {
    const previousContext = this.accessContextSnapshot
    const nextContext = createAccessContext(this.remote)
    const serverChanged = nextContext.baseUrl !== previousContext.baseUrl
    this.accessContextSnapshot = nextContext

    if (serverChanged) this.signOut()
    for (const listener of this.accessContextListeners) listener()
  }

  private markRestored() {
    const state = this.store.getState()
    if (state.snapshot.isRestored) return

    this.store.setState({
      snapshot: createSnapshot(state.snapshot.status, state.snapshot.user, true, state.snapshot.isAuthenticating),
    })
    this.resolveRestored()
  }

  private restoreAfterFailedLogin(previousState: SessionStoreState) {
    this.update({
      authorization: previousState.authorization,
      persistAuthorization: previousState.persistAuthorization,
      status: previousState.snapshot.status,
      user: previousState.snapshot.user,
      isAuthenticating: false,
    })
  }

  private throwIfAuthenticationAttemptChanged(attempt: number) {
    if (attempt !== this.authenticationAttempt) {
      throw new SessionAuthenticationCancelledError()
    }
  }

  private cancelAuthenticationAttempt() {
    if (!this.isAuthenticating) return

    ++this.authenticationAttempt
    this.authenticationAbortController?.abort(new SessionAuthenticationCancelledError())
    this.authenticationAbortController = null
  }

  private supersedeRestoration() {
    if (!this.isRestored) this.restorationSuperseded = true
  }
}

function createPersistedState(state: SessionStoreState): PersistedSessionState {
  const authorization = state.persistAuthorization ? state.authorization : undefined
  const status =
    state.snapshot.status === "authentication-required"
      ? "authentication-required"
      : authorization == null
        ? "signed-out"
        : "signed-in"

  return {
    authorization,
    status,
    user: state.snapshot.user,
  }
}

function mergePersistedState(
  persistedState: unknown,
  currentState: SessionStoreState,
  hasConfiguredAuthorization: boolean,
  configuredUser?: SessionUser
): SessionStoreState {
  if (hasConfiguredAuthorization) {
    return {
      ...currentState,
      snapshot: createSnapshot(currentState.snapshot.status, configuredUser ?? currentState.snapshot.user, false),
    }
  }

  const persisted = normalizePersistedState(persistedState)
  if (persisted == null) {
    return {
      ...currentState,
      snapshot: createSnapshot(currentState.snapshot.status, configuredUser ?? currentState.snapshot.user, false),
    }
  }

  const authorization = normalizeAuthorization(persisted.authorization)
  const status =
    persisted.status === "authentication-required"
      ? "authentication-required"
      : authorization == null
        ? "signed-out"
        : "signed-in"

  return {
    authorization,
    persistAuthorization: authorization != null,
    snapshot: createSnapshot(status, configuredUser ?? persisted.user, false),
  }
}

function normalizePersistedState(value: unknown): PersistedSessionState | null {
  if (value == null || typeof value !== "object") return null

  const state = value as Partial<PersistedSessionState>
  if (state.status !== "signed-out" && state.status !== "signed-in" && state.status !== "authentication-required") {
    return null
  }

  return {
    authorization: typeof state.authorization === "string" ? state.authorization : undefined,
    status: state.status,
    user: normalizeSessionUser(state.user),
  }
}

function normalizeSessionUser(value: unknown): SessionUser | null {
  if (value == null || typeof value !== "object") return null
  const user = value as Partial<SessionUser>
  if (typeof user.username !== "string" || user.username.length === 0) return null

  return {
    username: user.username,
    ...(typeof user.imageUrl === "string" && user.imageUrl.length > 0 ? { imageUrl: user.imageUrl } : {}),
  }
}

function normalizeAuthorization(value?: string) {
  const authorization = value?.trim()
  return authorization == null || authorization.length === 0 ? undefined : authorization
}

function createSnapshot(
  status: SessionStatus,
  user: SessionUser | null,
  isRestored: boolean,
  isAuthenticating = false
): SessionSnapshot {
  return Object.freeze({
    status,
    user: user == null ? null : Object.freeze({ ...user }),
    isRestored,
    isAuthenticating,
  })
}

function snapshotsEqual(left: SessionSnapshot, right: SessionSnapshot) {
  return (
    left.status === right.status &&
    left.isRestored === right.isRestored &&
    left.isAuthenticating === right.isAuthenticating &&
    left.user?.username === right.user?.username &&
    left.user?.imageUrl === right.user?.imageUrl
  )
}

function createCloudLoginRequest(
  credentials: BasicCredentials,
  productVersion: string | null,
  appIdentifier: string | null,
  options: SessionLoginOptions
): CloudLoginRequest {
  return {
    productVersion,
    userId: credentials.username.trim(),
    password: credentials.password,
    nietAutoLogin: true,
    appLogin: true,
    isExternalUser: false,
    authenticationToken: null,
    delayedLicense: false,
    appIdentifier,
    keepDuplicateUser: false,
    appAutoLogin: options.rememberAuthentication ?? false,
  }
}

function parseCloudLoginResult(
  value: unknown
): { success: true; authenticationToken: string; userId?: string } | { success: false; failure: SessionLoginFailure } {
  if (value == null || typeof value !== "object") {
    throw new RemoteProtocolError("Cloud.Server Login returned an invalid result.")
  }

  const result = value as CloudLoginResult
  if (typeof result.success !== "boolean") {
    throw new RemoteProtocolError("Cloud.Server Login result did not contain a boolean success value.")
  }

  if (!result.success) {
    return createLoginFailureFromWire(result.failure)
  }

  const authenticationToken = normalizeAuthorizationToken(result.authenticationToken)
  if (authenticationToken == null) {
    throw new RemoteProtocolError("Cloud.Server Login succeeded without returning an authentication token.")
  }

  const userId = typeof result.userId === "string" && result.userId.trim().length > 0 ? result.userId.trim() : undefined

  return { success: true, authenticationToken, userId }
}

function createLoginFailureFromWire(value: unknown): { success: false; failure: SessionLoginFailure } {
  if (value == null || typeof value !== "object") {
    return createLoginFailure("unknown")
  }

  const failure = value as { reason?: unknown; messages?: unknown }
  const messages = Array.isArray(failure.messages)
    ? failure.messages.filter((message): message is string => typeof message === "string")
    : []

  return createLoginFailure(normalizeLoginFailureReason(failure.reason), messages)
}

function createLoginFailure(
  reason: SessionLoginFailureReason,
  messages: readonly string[] = []
): { success: false; failure: SessionLoginFailure } {
  return Object.freeze({
    success: false,
    failure: Object.freeze({
      reason,
      messages: Object.freeze([...messages]),
    }),
  })
}

function normalizeLoginFailureReason(value: unknown): SessionLoginFailureReason {
  if (typeof value === "number") {
    return (
      ([
        "unknown",
        "no-client-id",
        "incomplete-authentication-data",
        "unknown-authentication-data",
        "external-internal-user-conflict",
        "session-lost-and-not-allowed-to-restore",
      ][value] as SessionLoginFailureReason | undefined) ?? "unknown"
    )
  }

  if (typeof value !== "string") return "unknown"
  switch (value.toUpperCase()) {
    case "NO_CLIENT_ID":
      return "no-client-id"
    case "INCOMPLETE_AUTHENTICATION_DATA":
      return "incomplete-authentication-data"
    case "UNKNOWN_AUTHENTICATION_DATA":
      return "unknown-authentication-data"
    case "EXTERNAL_INTERNAL_USER_CONFLICT":
      return "external-internal-user-conflict"
    case "SESSION_LOST_AND_NOT_ALLOWED_TO_RESTORE":
      return "session-lost-and-not-allowed-to-restore"
    default:
      return "unknown"
  }
}

function normalizeAuthorizationToken(value: unknown) {
  if (typeof value !== "string") return undefined
  const token = value.trim()
  return token.length > 0 ? token : undefined
}

function createBearerAuthorization(token: string) {
  const prefixed = /^bearer\s+(.+)$/i.exec(token)
  return `Bearer ${prefixed?.[1] ?? token}`
}

function createAuthenticatedUser(username: string, imageUrl?: string): SessionUser {
  return Object.freeze({
    username,
    ...(imageUrl == null ? {} : { imageUrl }),
  })
}

function createAccessContext(remote: RemoteClient): SessionAccessContext {
  return Object.freeze({
    revision: getRemoteRevision(remote),
    baseUrl: remote.getBaseUrl(),
  })
}

function forwardAbort(signal: AbortSignal | undefined, controller: AbortController) {
  if (signal == null) return () => undefined

  const abort = () => controller.abort(signal.reason)
  if (signal.aborted) {
    abort()
    return () => undefined
  }

  signal.addEventListener("abort", abort, { once: true })
  return () => signal.removeEventListener("abort", abort)
}
